import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Tag } from "@threadws/jute";
import { useEffect, useState } from "react";
import type { AgentRun } from "../../api/generated";
import { agentRunsGetOptions, agentRunsGetQueryKey, planningPlanMutation } from "../../api/generated/@tanstack/react-query.gen";
import { CHANNELS } from "../../realtime/ablyClient";
import { useChannel } from "../../realtime/useChannel";
import { today, type Day } from "../../utils/calendarDay";

const isWorking = (run: AgentRun | undefined) => !run || run.status === "queued" || run.status === "running";

const count = (n: number, what: string) => `${n} ${what}${n === 1 ? "" : "s"}`;

interface DayToolbarProps {
  day: Day;
  events: number;
  planned: number;
}

/**
 * Above the day's grid: how many events and planned blocks it has, and a button asking the day
 * planner to fit the day's tasks into its free time. The button follows the run live and shows
 * its reply; the new plan arrives by itself (see `usePlannedBlocks`). No button for past days.
 */
export function DayToolbar({ day, events, planned }: DayToolbarProps) {
  const queryClient = useQueryClient();
  const [runId, setRunId] = useState<string | null>(null);
  const plan = useMutation({ ...planningPlanMutation(), onSuccess: ({ runId }) => setRunId(runId) });
  const { data: run } = useQuery({
    ...agentRunsGetOptions({ path: { id: runId ?? "" } }),
    enabled: runId !== null,
    // Ably normally brings the result; this is in case the live connection drops.
    refetchInterval: (query) => (isWorking(query.state.data) ? 5_000 : false),
  });
  useChannel(CHANNELS.agentRuns, ({ data }) => {
    const updated = data as AgentRun;
    if (updated.id === runId) queryClient.setQueryData(agentRunsGetQueryKey({ path: { id: updated.id } }), updated);
  });
  // A reply belongs to the day it was for.
  useEffect(() => {
    setRunId(null);
    plan.reset();
  }, [day]); // eslint-disable-line react-hooks/exhaustive-deps

  const working = plan.isPending || (runId !== null && isWorking(run));
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap gap-2">
        <Tag tone="neutral">{count(events, "event")}</Tag>
        {planned > 0 && <Tag tone="neutral">{`${planned} planned`}</Tag>}
      </div>
      {day >= today() && (
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-3">
          <p aria-live="polite" className="min-w-0 text-right text-sm text-pretty">
            {plan.error && <span className="text-danger">{plan.error.message}</span>}
            {runId && working && <span className="text-text-secondary motion-safe:animate-pulse">Finding time for your tasks…</span>}
            {run?.status === "failed" && <span className="text-danger">Couldn't plan the day: {run.error}</span>}
            {run?.status === "succeeded" && <span className="text-text-secondary">{run.output}</span>}
          </p>
          <Button variant="secondary" icon="" disabled={working} onClick={() => plan.mutate({ path: { day } })}>
            {working ? "Planning…" : day === today() ? "Plan my day" : "Plan this day"}
          </Button>
        </div>
      )}
    </div>
  );
}
